import { useState, useEffect, useRef, useCallback, useMemo } from "react";

/**
 * Filter state for a report category — a filter chosen for "attendance"
 * doesn't leak into "payroll", matching the per-type filterFields model.
 */
const useReportFilters = (categoryId) => {
  const [draft, setDraft] = useState({});
  const [applied, setApplied] = useState({});

  // Mirrors `draft` synchronously (updated inside the setState updater, not
  // via a useEffect) so `apply` can read the very latest value even when
  // called from a caller that captured an older `apply`/`draft` closure —
  // e.g. FiltersPanel flushes an open multi-select via a synthetic mousedown
  // right before calling apply(); that commit lands as its own setField call,
  // one render after handleApply's own closure was created.
  const draftRef = useRef(draft);

  useEffect(() => {
    setDraft({});
    setApplied({});
    draftRef.current = {};
  }, [categoryId]);

  const setField = useCallback((key, value) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      draftRef.current = next;
      return next;
    });
  }, []);

  // Stable identity (no `draft` dependency) so a setTimeout(apply, 0)
  // queued before a pending re-render still applies the up-to-date draft.
  const apply = useCallback(() => setApplied(draftRef.current), []);
  const reset = useCallback(() => { setDraft({}); setApplied({}); draftRef.current = {}; }, []);

  // employee / company use { mode: "all"|"specific"|"multiple", ids: [] } —
  // "all" (or unset) omits the filter entirely, "specific" sends a single
  // id, "multiple" sends an array. reportService.js's buildEmployeeScope/
  // CompanyScopeWhere helpers consume exactly this employee/employees and
  // company/companies shape.
  const scopedEntityToFilters = (scoped, singleKey, arrayKey) => {
    if (!scoped || scoped.mode === "all" || !scoped.ids?.length) return {};
    if (scoped.mode === "specific") return { [singleKey]: scoped.ids[0] };
    return { [arrayKey]: scoped.ids };
  };

  const requestFilters = useMemo(() => {
    // Maps UI draft keys -> backend filter keys consumed by reportService.js
    const f = {};
    if (applied.dateMode === "exact" && applied.specificDate) f.date = applied.specificDate;
    if (applied.dateRange?.from) f.startDate = applied.dateRange.from;
    if (applied.dateRange?.to) f.endDate = applied.dateRange.to;
    if (applied.payrollMonth) {
      const [y, m] = applied.payrollMonth.split("-");
      f.year = parseInt(y, 10);
      f.month = parseInt(m, 10);
    }
    Object.assign(f, scopedEntityToFilters(applied.employee, "employee", "employees"));
    Object.assign(f, scopedEntityToFilters(applied.company, "company", "companies"));
    ["department", "manager", "employmentStatus", "attendanceStatus", "leaveType", "search", "status"].forEach((k) => {
      if (applied[k]) f[k] = applied[k];
    });
    return f;
  }, [applied]);

  return { draft, applied, setField, apply, reset, requestFilters };
};

export default useReportFilters;
